<template>
  <v-dialog :model-value="modelValue" persistent :scrim="true" max-width="480" class="signup-gate-dialog"
    content-class="signup-gate-dialog-content" no-click-animation @click:outside.prevent.stop @keydown.esc.prevent.stop>
    <template #default>
      <v-card class="signup-gate-card">
        <v-card-title class="signup-gate-header">
          <h2 class="signup-gate-title">Sign Up to Continue Exploring</h2>
        </v-card-title>

        <v-card-text class="signup-gate-content">
          <p class="signup-gate-message">
            You've been exploring our bonds for a while. Sign up to unlock full access to detailed information, cashflow
            schedules, and secure investment options.
          </p>

          <div class="signup-gate-benefits">
            <div class="benefit-item">
              <v-icon aria-hidden="true" color="#12BF57" size="20">mdi-check-circle</v-icon>
              <span>Access detailed cashflow schedules</span>
            </div>
            <div class="benefit-item">
              <v-icon aria-hidden="true" color="#12BF57" size="20">mdi-check-circle</v-icon>
              <span>Download diligence documents</span>
            </div>
            <div class="benefit-item">
              <v-icon aria-hidden="true" color="#12BF57" size="20">mdi-check-circle</v-icon>
              <span>Invest securely in bonds</span>
            </div>
          </div>
        </v-card-text>

        <v-card-actions class="signup-gate-actions">
          <v-btn color="buttonBg" variant="flat" size="large" block class="signup-gate-button"
            @click="handleSignupClick">
            Signup Now
          </v-btn>
        </v-card-actions>
      </v-card>
    </template>
  </v-dialog>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRoute } from 'vue-router'
import { useUtmTracking } from '~/composables/useUtmTracking'

defineOptions({ name: 'SignupGateDialog' })

interface Props {
  modelValue: boolean
}

const props = defineProps<Props>()

const emit = defineEmits<{
  'update:modelValue': [value: boolean]
}>()

if (typeof window !== "undefined") {
  window.addEventListener('close-all-overlays', () => {
    emit('update:modelValue', false)
  });
}

const route = useRoute()
const { buildRedirectUrl } = useUtmTracking()
const { investorUrl } = useInvestorPlatform()

const signupUrl = computed(() => {
  const baseUrl = investorUrl('/login')
  const ctaName = 'signup-gate-engagement'
  return buildRedirectUrl(baseUrl, route.path, ctaName)
})

const handleSignupClick = () => {
  // Redirect to invest.equirize.com/login with UTM tracking
  window.open(signupUrl.value, '_blank')
}
</script>

<style scoped>
.signup-gate-dialog :deep(.v-overlay__scrim) {
  backdrop-filter: blur(4px) !important;
  -webkit-backdrop-filter: blur(4px) !important;
  background: rgba(0, 0, 0, 0.5) !important;
}

.signup-gate-dialog :deep(.v-overlay__content) {
  pointer-events: auto;
}

.signup-gate-dialog-content {
  pointer-events: auto;
}

.signup-gate-card {
  border-radius: 12px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.signup-gate-header {
  padding: 24px 24px 16px;
  border-bottom: 1px solid #EAF7FF;
}

.signup-gate-title {
  font-family: var(--font-family-primary, 'Inter', sans-serif);
  font-size: 20px;
  font-weight: 700;
  color: #1b2559;
  margin: 0;
  line-height: 28px;
}

.signup-gate-content {
  padding: 24px;
}

.signup-gate-message {
  font-family: var(--font-family-primary, 'Inter', sans-serif);
  font-size: 16px;
  font-weight: 400;
  color: #1b2559;
  line-height: 24px;
  margin: 0 0 20px;
}

.signup-gate-benefits {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 20px;
  padding: 16px;
  background: #F9FBFF;
  border-radius: 8px;
}

.benefit-item {
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: var(--font-family-primary, 'Inter', sans-serif);
  font-size: 14px;
  font-weight: 500;
  color: #1b2559;
  line-height: 20px;
}

.signup-gate-actions {
  padding: 16px 24px 24px;
  border-top: 1px solid #EAF7FF;
  justify-content: center;
}

.signup-gate-button {
  font-family: var(--font-family-primary, 'Inter', sans-serif);
  font-size: 16px;
  font-weight: 600;
  text-transform: none;
  letter-spacing: 0;
}

/* Mobile Responsive */
@media (max-width: 768px) {
  .signup-gate-header {
    padding: 20px 20px 16px;
  }

  .signup-gate-title {
    font-size: 18px;
  }

  .signup-gate-content {
    padding: 20px;
  }

  .signup-gate-message {
    font-size: 15px;
  }

  .signup-gate-actions {
    padding: 16px 20px 20px;
  }
}

@media (max-width: 480px) {
  .signup-gate-header {
    padding: 16px 16px 12px;
  }

  .signup-gate-title {
    font-size: 16px;
  }

  .signup-gate-content {
    padding: 16px;
  }

  .signup-gate-message {
    font-size: 14px;
  }

  .signup-gate-benefits {
    padding: 12px;
  }

  .benefit-item {
    font-size: 13px;
  }

  .signup-gate-actions {
    padding: 12px 16px 16px;
  }
}
</style>

<style>
/* Global styles for backdrop blur on body when dialog is open */
.signup-gate-dialog-open {
  overflow: hidden;
}

.signup-gate-dialog-open body {
  filter: blur(4px);
  pointer-events: none;
  user-select: none;
}
</style>
